<template>
	<div class="creation-wizard display-flex flex-wrap height-100">
		<component
			:is="stepComponent"
			:wizardStep="wizardStep"
			:previousSteps="previousSteps"
			@next-step="nextStep"
			@prev-step="prevStep"
			@update-state="updateState"
			@close-wizard="closeWizard"
			@workflow-created="workflowCreated"
			@start-creation-process="onStartCreationProcess"
			v-bind="componentProps"
			v-if="wizardStep"
		/>
	</div>
</template>

<script>
import CreationWizardService from './creation-wizard-service';
import { FIELDS_METADATA } from './creation-wizard-state.const';
import { CREATION_MODES, CREATION_WIZARD_STEP_CONFIG } from './creation-wizard.const';
import { getServiceData, getServiceDataBySlug } from '@/store/modules/apps/actions';
import { getService, getServiceBySlug } from '@/store/modules/apps/getters';
import { mapActionsShorthand, mapGettersShorthand } from '@/store/utils/mappers';

export default {
	name: 'CreationWizard',
	props: {
		initialState: { type: Object, default: () => ({}) },
		extensionMode: { type: Boolean }, // for routing purposes between wizard steps only, because there are steps relevant only for extension
		embeddedMode: { type: Boolean },
		mode: { type: String, default: CREATION_MODES.WORKFLOW, validator: (val) => Object.values(CREATION_MODES).includes(val) }, // workflow / button dropdown
	},
	computed: {
		...mapGettersShorthand({ getService, getServiceBySlug }),
		currentStepConfig() {
			return CREATION_WIZARD_STEP_CONFIG[this.wizardStep];
		},
		stepComponent() {
			return this.currentStepConfig.component;
		},
		componentProps() {
			return Object.keys(this.stepComponent.props || {})
				.filter((key) => key in this)
				.reduce((curProps, curProp) => ({ ...curProps, [curProp]: this[curProp] }), {});
		},
		getNextStepParams() {
			// these are the params the getNextStep needs from the wizard to decide which step is next
			return (this.currentStepConfig.getNextStepArgs || []).reduce(
				(params, curParamKey) => ({ ...params, [curParamKey]: this[curParamKey] }),
				{}
			);
		},
		currentUrl() {
			// in case we got here from ext, we received some pre-selected values
			return this.initialState?.currentUrl;
		},
		selectedApp() {
			return this.getService(this.selectedAppId);
		},
		initialElementType() {
			return this.initialState?.elementType;
		},
	},
	methods: {
		...mapActionsShorthand({ getServiceData, getServiceDataBySlug }),
		async nextStep() {
			const { destination, keepLastStepInHistory } = await this.currentStepConfig.getNextStep(this.getNextStepParams);
			if (keepLastStepInHistory) {
				this.previousSteps.push(this.wizardStep);
			}
			this.wizardStep = destination;
		},
		prevStep() {
			this.wizardStep = this.previousSteps.pop();
		},
		resetState(depField) {
			this.updateState({ key: depField, value: FIELDS_METADATA[depField].default() });
		},
		updateState({ key, value }) {
			const stateHasChanged = value !== this[key];
			this.$set(this, key, value);
			if (stateHasChanged) {
				FIELDS_METADATA[key]?.deps?.forEach(this.resetState);
			}
		},
		async setInitialState() {
			// in case we got here from ext, we received some pre-selected values
			const { featureTypeId, appId, slug, serviceWorkspaceId, projectId, toolPlacement, elementType, selectedTabIndex, disabledTabs } =
				this.initialState;
			if (featureTypeId) {
				this.selectedFeatureTypeId = featureTypeId;
			}
			if (slug) {
				await this.getServiceDataBySlug({ slug });
				this.selectedAppId = this.getServiceBySlug(slug).id;
			}
			if (appId) {
				await this.getServiceData({ id: appId });
				this.selectedAppId = appId;
			}
			if (serviceWorkspaceId) {
				this.selectedServiceWorkspaceId = serviceWorkspaceId;
			}
			if (projectId) {
				this.selectedProjectId = projectId;
			}
			if (toolPlacement) {
				this.selectedToolPlacementId = toolPlacement;
			}
			if (elementType) {
				this.selectedElementType = elementType;
			}
			if (selectedTabIndex) {
				this.selectedTabIndex = selectedTabIndex;
			}
			if (disabledTabs) {
				this.disabledTabs = disabledTabs;
			}
		},
		closeWizard() {
			// if someone (CreationWizardDialog?) is showing the wizard, let's ask him to close it
			this.$emit('close-wizard');
		},
		workflowCreated(workflow) {
			this.$emit('workflow-created', workflow);
		},
		onStartCreationProcess(creationProcessMessage) {
			this.$emit('start-creation-process', creationProcessMessage);
		},
	},
	data() {
		return CreationWizardService.getWizardState();
	},
	async created() {
		await this.setInitialState();
		this.wizardStep = CreationWizardService.getInitialWizardStep(this);
	},
};
</script>

<style lang="scss" scoped>
@import './assets/creation-wizard-vars.scss';
.creation-wizard {
	overflow-y: auto;
}
</style>
